import { useState } from "react"
import { Loader2, Check, ChevronDown, ChevronRight } from "lucide-react"
import { TOOL_LABELS } from "@/lib/ai-types"
import type { RichToolCall } from "@/lib/ag-ui-types"
import { RichToolCardRenderer } from "./tool-cards/tool-card-registry"

interface AiStepIndicatorProps {
  toolCall: RichToolCall
}

function formatElapsed(startedAt: number, finishedAt: number | null): string {
  const end = finishedAt || Date.now()
  const ms = end - startedAt
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

export function AiStepIndicator({ toolCall }: AiStepIndicatorProps) {
  const [expanded, setExpanded] = useState(false)
  const label = TOOL_LABELS[toolCall.name] || toolCall.name
  const isDone = toolCall.status === "done"
  const elapsed = formatElapsed(toolCall.startedAt, toolCall.finishedAt)

  return (
    <div className="my-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors"
        style={{ color: "var(--text-muted)" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bg-surface-raised)" }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent" }}
      >
        {isDone ? (
          <Check size={12} style={{ color: "var(--color-success)" }} />
        ) : (
          <Loader2 size={12} className="animate-spin" style={{ color: "var(--primary)" }} />
        )}
        <span className="flex-1" style={{ color: "var(--text-muted)" }}>{label}</span>
        <span className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>{elapsed}</span>
        {isDone ? (
          expanded
            ? <ChevronDown size={12} style={{ color: "var(--text-muted)" }} />
            : <ChevronRight size={12} style={{ color: "var(--text-muted)" }} />
        ) : null}
      </button>

      {expanded && isDone && (
        <div className="ml-5 mt-1">
          <RichToolCardRenderer toolCall={toolCall} />
        </div>
      )}
    </div>
  )
}

interface AiStepGroupProps {
  toolCalls: Map<string, RichToolCall>
}

export function AiStepGroup({ toolCalls }: AiStepGroupProps) {
  const entries = Array.from(toolCalls.values())
  if (entries.length === 0) return null

  const allDone = entries.every((tc) => tc.status === "done")

  return (
    <div
      className="my-2 rounded-xl px-1 py-1"
      style={{ border: "1px solid var(--border)", background: "var(--bg-surface-raised)" }}
    >
      <div className="flex items-center gap-2 px-2 py-1">
        {allDone ? (
          <Check size={12} style={{ color: "var(--color-success)" }} />
        ) : (
          <Loader2 size={12} className="animate-spin" style={{ color: "var(--primary)" }} />
        )}
        <span className="text-xs font-medium" style={{ color: "var(--text-muted)" }}>
          {allDone ? "Przetworzono" : "Przetwarzanie..."}
        </span>
      </div>
      {entries.map((tc) => (
        <AiStepIndicator key={tc.id} toolCall={tc} />
      ))}
    </div>
  )
}
